import { fetcherConfig } from '../lib/api-fetcher';
import { useState } from 'react';
import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { usePageTitle } from '../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { Button } from '../components/ui/Button';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { FormField, Input } from '../components/ui/FormField';
import { AuthLayout } from '../components/layout/AuthLayout';
import { Stack } from '../components/layout/Stack';
import { createAuthResource } from '@repo/api-client';
import { getSession, setSession } from '../lib/session';
import { sessionQueryKey } from '../lib/query-client';

const authResource = createAuthResource(fetcherConfig);

function strengthScore(pw: string): number {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  return score;
}

const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
const strengthColors = ['', 'bg-danger-500', 'bg-warning-500', 'bg-warning-500', 'bg-success-500', 'bg-success-500'];

const forcePasswordChangeFormConfig = defineFormPage({
  resource: 'auth-force-password-change',
  defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  schema: z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Minimum 8 characters'),
    confirmPassword: z.string(),
  }),
});

export const Route = createFileRoute('/force-password-change')({
  beforeLoad: () => {
    const session = getSession();
    if (!session) {
      throw redirect({ to: '/login' });
    }
    if (!session.requiresPasswordReset) {
      throw redirect({ to: '/' });
    }
  },
  component: ForcePasswordChangePage,
});

function ForcePasswordChangePage() {
  usePageTitle('Force Password Change');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [submitError, setSubmitError] = useState<string | undefined>();

  const { form, isSubmitting, fieldError: fe } = useFormPage(
    forcePasswordChangeFormConfig,
    { mutateAsync: async (v) => v },
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.setErrorMap({ onSubmit: undefined });
    setSubmitError(undefined);
    await form.handleSubmit();
    if (!form.state.canSubmit) return;
    try {
      await authResource.forceChange({
        currentPassword: form.getFieldValue('currentPassword'),
        newPassword: form.getFieldValue('newPassword'),
      });
      const session = getSession();
      if (session) {
        const updated = { ...session, requiresPasswordReset: false };
        setSession(updated);
        qc.setQueryData(sessionQueryKey, updated);
      }
      void navigate({ to: '/' });
    } catch (err: unknown) {
      console.error('Failed to change password:', err);
      const msg = err instanceof Error ? err.message : 'Failed to change password';
      setSubmitError(msg);
    }
  };

  return (
    <AuthLayout title="Set new password" subtitle="Your account requires a password change before you can continue.">
      <form onSubmit={handleSubmit}>
        <Stack gap="4">
          <form.Field
            name="currentPassword"
            validators={{ onChange: z.string().min(1, 'Current password is required') }}
          >
            {(field: any) => {
              const error = fe(field.state.meta);
              return (
                <FormField label="Current password" required error={error} htmlFor="current-password">
                  <Input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    disabled={isSubmitting}
                    error={!!error}
                    placeholder="••••••••"
                  />
                </FormField>
              );
            }}
          </form.Field>

          <form.Field
            name="newPassword"
            validators={{ onChange: z.string().min(8, 'Minimum 8 characters') }}
          >
            {(field: any) => {
              const score = strengthScore(field.state.value);
              const error = fe(field.state.meta);
              return (
                <FormField label="New password" required error={error} htmlFor="new-password">
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    disabled={isSubmitting}
                    error={!!error}
                    placeholder="Min. 8 characters"
                  />
                  {field.state.value && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 flex gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-colors ${i <= score ? strengthColors[score] : 'bg-surface-3'}`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-text-muted">{strengthLabels[score]}</span>
                    </div>
                  )}
                </FormField>
              );
            }}
          </form.Field>

          <form.Field
            name="confirmPassword"
            validators={{
              onChange: ({ value, fieldApi }: { value: any; fieldApi: any }) => {
                const newPw = fieldApi.form.getFieldValue('newPassword');
                if (value && value !== newPw) return 'Passwords do not match';
                return undefined;
              },
            }}
          >
            {(field: any) => {
              const error = fe(field.state.meta);
              return (
                <FormField label="Confirm new password" required error={error} htmlFor="confirm-password">
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    disabled={isSubmitting}
                    error={!!error}
                    placeholder="••••••••"
                  />
                </FormField>
              );
            }}
          </form.Field>

          {submitError && (
            <p className="text-xs text-danger-text" role="alert">
              {submitError}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isSubmitting}
            className="w-full"
          >
            Set new password
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}